import React, { Fragment, memo, useMemo, useCallback, useEffect, useState } from 'react';
import { Tabs, Modal } from '@Sage/hoi-poi-ui';
import { cleanEmptyValues, isEqual } from '@Sage/ssm-utils';
import PropTypes from 'prop-types';

import Literal from 'components/literal';
import ScrollBar from 'components/ScrollBar';
import { t } from 'utils/getLiteral';

import { ButtonCrud, TYPE_DEFAULT, TYPE_PRIMARY } from '../buttons/ButtonCrud';

import './styles.scss';

const Crud = memo(
    ({
        activeTab,
        btnSaveDisabled,
        buttonsDisabled,
        children,
        footerRenderer,
        onCancel,
        onClickTab = () => null,
        onSave,
        tabs,
        title = '',
        wide,
        originalData = {},
        data = {},
    }) => {
        const classNames = ['crud'];
        const [cancelModal, setCancelModal] = useState({ isOpen: false });

        if (wide) {
            classNames.push('wide');
        }

        const withTabs = tabs;

        const bodyClassNames = ['crud-body'];

        if (footerRenderer) {
            bodyClassNames.push('with-footer');
        }

        const finalTabs = useMemo(
            () => (tabs ? [...tabs.map((tab) => ({ key: tab?.id, title: tab?.label }))] : null),
            [tabs],
        );

        const checkEquals = useCallback(
            (originalData, data) => {
                const finalData = cleanEmptyValues(data);
                const finalOriginalData = cleanEmptyValues(originalData);
                if (originalData && data && !isEqual(finalOriginalData, finalData)) {
                    return setCancelModal({ isOpen: true });
                } else {
                    onCancel && onCancel();
                }
            },
            [onCancel],
        );

        const onKeyPress = useCallback(
            (e) => {
                if (e.key === 'Escape') {
                    checkEquals(data, originalData);
                }
            },
            [originalData, data, checkEquals],
        );

        const onClickCancel = useCallback(
            () => checkEquals(data, originalData),
            [originalData, data, checkEquals],
        );

        useEffect(() => {
            document.addEventListener('keydown', onKeyPress, false);
            return () => document.removeEventListener('keydown', onKeyPress, false);
        }, [onKeyPress]);

        const onRequestClose = useCallback(() => setCancelModal({ isOpen: false }), []);

        const onConfirmClose = useCallback(() => {
            setCancelModal({ isOpen: false });
            onCancel && onCancel();
        }, [onCancel]);

        return (
            <Fragment>
                <div className={classNames.join(' ')}>
                    <div className="crud-header">
                        <div className="crud-header-title float-left">{title}</div>
                        <div className="crud-header-btns float-right">
                            <ButtonCrud
                                type={TYPE_DEFAULT}
                                onClick={onClickCancel}
                                disabled={buttonsDisabled}
                                dataTest="crud-action-cancel"
                            >
                                <Literal literal="cfm_action_cancel" />
                            </ButtonCrud>
                            <ButtonCrud
                                type={TYPE_PRIMARY}
                                onClick={onSave}
                                disabled={buttonsDisabled || btnSaveDisabled}
                                dataTest="crud-action-save"
                            >
                                <Literal literal="cfm_action_save" />
                            </ButtonCrud>
                        </div>
                    </div>
                    <div className={bodyClassNames.join(' ')}>
                        {withTabs && (
                            <Tabs
                                className="crud-tabs"
                                tabs={finalTabs}
                                activeKey={activeTab}
                                onChange={onClickTab}
                            />
                        )}
                        <ScrollBar
                            style={{
                                height: withTabs ? 'calc(100% - 40px)' : '100%',
                                width: '100%',
                            }}
                        >
                            <div className="crud-body-inside">{children}</div>
                        </ScrollBar>
                    </div>
                    {footerRenderer && (
                        <div className="crud-footer">
                            <div className="crud-footer-inside">{footerRenderer()}</div>
                        </div>
                    )}
                </div>
                <Modal
                    overlayClassName="crud-modal__overlay"
                    isOpen={cancelModal.isOpen}
                    onConfirm={onConfirmClose}
                    size="small"
                    onRequestClose={onRequestClose}
                    onCancel={onRequestClose}
                    title={t('cfm_label_discard_unsaved_changes')}
                    confirmText={t('cfm_action_discard_changes')}
                    cancelText={t('cfm_action_cancel')}
                />
            </Fragment>
        );
    },
);

Crud.propTypes = {
    activeTab: PropTypes.string,
    btnSaveDisabled: PropTypes.bool,
    buttonsDisabled: PropTypes.bool,
    children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
    footerRenderer: PropTypes.func,
    onCancel: PropTypes.func.isRequired,
    onClickTab: PropTypes.func,
    onSave: PropTypes.func.isRequired,
    tabs: PropTypes.arrayOf(PropTypes.shape({})),
    title: PropTypes.string,
    wide: PropTypes.bool,
};

export default Crud;
